(() => {
  'use strict';
  const safeURL = (value, origin) => {
    if (typeof value !== 'string' || !value.trim()) return null;
    try { const url = new URL(value, origin); return url.origin === origin && /^https?:$/.test(url.protocol) ? url.pathname + url.search : null; } catch { return null; }
  };
  const years = vehicle => vehicle.start ? `${vehicle.start}${vehicle.end && vehicle.end !== vehicle.start ? `–${vehicle.end}` : ''}` : '';
  // One choice per published Véhicule entry and product that references it (Shopify custom.vehicules).
  function catalogueChoices(vehicles, products, origin) {
    const byId = new Map(vehicles.filter(v => v.id && v.brand && v.name).map(v => [v.id, v]));
    const choices = products.flatMap(product => {
      const url = safeURL(product.url, origin);
      if (!url) return [];
      return (product.vehicles || []).map(id => byId.get(id)).filter(Boolean).map(vehicle => ({
        id: `${vehicle.id}:${product.id}`, vehicle: vehicle.id, brand: vehicle.brand, model: vehicle.model, generation: vehicle.generation,
        modelLabel: [vehicle.name, years(vehicle), product.detail].filter(Boolean).join(' · '),
        label: `${vehicle.brand} ${vehicle.name}${years(vehicle) ? ` (${years(vehicle)})` : ''}`, url, approved: true, materials: product.materials || ''
      }));
    });
    // Two products for the same vehicle and part are told apart by their materials.
    const count = choices.reduce((all, choice) => all.set(choice.brand + choice.modelLabel, (all.get(choice.brand + choice.modelLabel) || 0) + 1), new Map());
    return choices.map(({ materials, ...choice }) => count.get(choice.brand + choice.modelLabel) > 1 && materials ? { ...choice, modelLabel: `${choice.modelLabel} · ${materials}` } : choice);
  }
  const savedChoice = (choices, saved) => saved && choices.find(v => v.brand === saved.brand && v.id === saved.id);
  if (typeof module !== 'undefined' && module.exports) { module.exports = { safeURL, catalogueChoices, savedChoice }; return; }
  const read = (root, selector) => { try { return JSON.parse(root.querySelector(selector)?.textContent || '[]'); } catch { return []; } };
  const stored = key => { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } };
  class VehicleFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const form = this.querySelector('form');
      const brand = this.querySelector('[data-brand]'), model = this.querySelector('[data-model]');
      if (!form || !brand || !model) return;
      const remote = this.querySelector('[data-catalogue-products][data-src]:not([data-loaded])');
      if (remote) { this.loadProducts(remote); return; }
      this.ready = true;
      const language = document.documentElement.lang.split('-')[0];
      this.vehicles = catalogueChoices(read(this, '[data-vehicles]'), read(this, '[data-catalogue-products]'), location.origin);
      if (!this.vehicles.length) return;
      const status = this.querySelector('[data-finder-status], .finder-message');
      const storageKey = this.dataset.storageKey || 'mst-vehicle-v1';
      const placeholders = new Map([brand, model].map(s => [s, s.options[0].text]));
      const fill = (select, entries) => {
        select.replaceChildren(new Option(placeholders.get(select), ''));
        entries.forEach(([label, value]) => select.add(new Option(label, value)));
        select.disabled = !entries.length;
      };
      const sync = () => form.classList.toggle('is-complete', Boolean(brand.value && model.value));
      const updateBrand = () => {
        fill(model, this.vehicles.filter(v => v.brand === brand.value).sort((a, b) => (a.model || '').localeCompare(b.model || '', language, { numeric: true }) || a.modelLabel.localeCompare(b.modelLabel, language, { numeric: true })).map(v => [v.modelLabel, v.id]));
        sync();
      };
      const brands = [...new Set(this.vehicles.map(v => v.brand))].sort((a, b) => a.localeCompare(b, language));
      fill(brand, brands.map(v => [v, v]));
      const announce = value => { if (status) { status.hidden = false; status.textContent = value || ''; } };
      brand.addEventListener('change', () => { updateBrand(); announce(this.dataset.statusBrand?.replace('{{ brand }}', brand.value)); });
      model.addEventListener('change', () => { sync(); announce(model.value ? this.dataset.statusReady : ''); });
      form.addEventListener('submit', event => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        const vehicle = this.vehicles.find(v => v.brand === brand.value && v.id === model.value);
        const destination = vehicle && safeURL(vehicle.url, location.origin);
        if (!destination) return;
        const selection = vehicle;
        try {
          localStorage.removeItem('mst-home-search-v1');
          localStorage.setItem(storageKey, JSON.stringify(selection));
        } catch { /* Navigation works when storage is blocked. */ }
        window.dispatchEvent(new CustomEvent('mst:vehicle-change', { detail: selection }));
        location.assign(destination);
      });
      const match = savedChoice(this.vehicles, stored(storageKey));
      if (match) { brand.value = match.brand; updateBrand(); model.value = match.id; sync(); }
      else {
        const partial = stored('mst-home-search-v1');
        if (partial && brands.includes(partial.brand)) { brand.value = partial.brand; updateBrand(); }
      }
      form.hidden = false;
      const fallback = this.querySelector('.home-finder-fallback, .finder-fallback');
      if (fallback) fallback.hidden = true;
      this.dispatchEvent(new CustomEvent('mst:finder-ready', { bubbles: true }));
    }
    // Past the first page of a collection, the products of the picker are out of range of the page parameter: the section holding them is fetched on its own.
    async loadProducts(script) {
      script.dataset.loaded = 'true';
      try {
        const response = await fetch(script.dataset.src, { headers: { Accept: 'text/html' } });
        if (response.ok) {
          const section = new DOMParser().parseFromString(await response.text(), 'text/html');
          const products = section.querySelector('[data-catalogue-products]:not([data-src])');
          if (products) script.textContent = products.textContent;
        }
      } catch { /* The link to the catalogue stays visible when the request fails. */ }
      this.connectedCallback();
    }
  }
  customElements.define('vehicle-finder', VehicleFinder);
  customElements.define('home-vehicle-finder', class extends VehicleFinder {});
})();
